<template>
  <div id="FacultiesProgrammesAdmin" class="container-fluid">
    <!-- Page Header & Stats -->
    <div class="row mb-3">
      <div class="col-md-6">
        <div class="pageTitle">Faculties & Programs</div>
        <p class="text-muted mb-0">Manage academic faculties and all degree programs under each.</p>
      </div>
      <div class="col-md-6 text-md-right mt-3 mt-md-0">
        <button class="btn btn-outline-primary mr-2" @click="openUploadModal">
          <b-icon-upload class="mr-1"></b-icon-upload> Upload CSV
        </button>
        <button class="btn btn-outline-success mr-2" @click="openFacultyModal('create')">
          <b-icon-plus-circle class="mr-1"></b-icon-plus-circle> New Faculty
        </button>
        <button class="btn btn-success" @click="openProgrammeModal('create')">
          <b-icon-plus-circle class="mr-1"></b-icon-plus-circle> New Program
        </button>
      </div>
    </div>

    <!-- Quick Stats Cards -->
    <div class="row mb-4">
      <div class="col-sm-6 col-lg-3 mb-2">
        <div class="card shadow-sm border-0 stat-card">
          <div class="card-body d-flex align-items-center">
            <div class="stat-icon bg-primary text-white mr-3">
              <b-icon-building></b-icon-building>
            </div>
            <div>
              <div class="stat-label text-muted text-uppercase">Total Faculties</div>
              <div class="stat-value font-weight-bold">{{ faculties.length }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-lg-3 mb-2">
        <div class="card shadow-sm border-0 stat-card">
          <div class="card-body d-flex align-items-center">
            <div class="stat-icon bg-success text-white mr-3">
              <b-icon-book></b-icon-book>
            </div>
            <div>
              <div class="stat-label text-muted text-uppercase">Total Programs</div>
              <div class="stat-value font-weight-bold">{{ totalProgrammesCount }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-lg-3 mb-2">
        <div class="card shadow-sm border-0 stat-card">
          <div class="card-body d-flex align-items-center">
            <div class="stat-icon bg-info text-white mr-3">
              <b-icon-check2-circle></b-icon-check2-circle>
            </div>
            <div>
              <div class="stat-label text-muted text-uppercase">Active Programs</div>
              <div class="stat-value font-weight-bold">{{ activeProgrammesCount }}</div>
            </div>
          </div>
        </div>
      </div>
      <!-- <div class="col-sm-6 col-lg-3 mb-2">
        <div class="card shadow-sm border-0 stat-card">
          <div class="card-body d-flex align-items-center">
            <div class="stat-icon bg-secondary text-white mr-3">
              <b-icon-diagram-3></b-icon-diagram-3>
            </div>
            <div>
              <div class="stat-label text-muted text-uppercase">Avg Programs/Faculty</div>
              <div class="stat-value font-weight-bold">
                {{ faculties.length > 0 ? (totalProgrammesCount / faculties.length).toFixed(1) : 0 }}
              </div>
            </div>
          </div>
        </div>
      </div> -->
    </div>

    <!-- Search and Controls Bar -->
    <div class="row mb-3">
      <div class="col-md-7">
        <div class="input-group">
          <div class="input-group-prepend">
            <span class="input-group-text bg-white border-right-0">
              <b-icon-search></b-icon-search>
            </span>
          </div>
          <input
            type="text"
            v-model="searchQuery"
            class="form-control border-left-0"
            placeholder="Search by faculty code, faculty name, program code, or program name..."
          />
          <div class="input-group-append" v-if="searchQuery">
            <button class="btn btn-outline-secondary" type="button" @click="searchQuery = ''">
              Clear
            </button>
          </div>
        </div>
      </div>
      <div class="col-md-5 text-md-right mt-2 mt-md-0">
        <button class="btn btn-sm btn-light border mr-2" @click="expandAll(true)">
          <b-icon-chevron-bar-expand class="mr-1"></b-icon-chevron-bar-expand> Expand All
        </button>
        <button class="btn btn-sm btn-light border mr-2" @click="expandAll(false)">
          <b-icon-chevron-bar-contract class="mr-1"></b-icon-chevron-bar-contract> Collapse All
        </button>
        <button class="btn btn-sm btn-light border" @click="loadFaculties" title="Refresh">
          <b-icon-arrow-clockwise :class="{ 'spin-icon': loading }"></b-icon-arrow-clockwise> Refresh
        </button>
      </div>
    </div>

    <!-- Main Content: Faculties and Nested Programs -->
    <div v-if="loading && faculties.length === 0" class="text-center py-5">
      <b-spinner variant="success" label="Loading faculties and programs..."></b-spinner>
      <p class="text-muted mt-2">Loading Faculties & Programs...</p>
    </div>

    <div v-else-if="filteredFaculties.length === 0" class="card border-0 shadow-sm text-center py-5">
      <div class="card-body">
        <b-icon-folder-x class="text-muted mb-3" font-scale="3"></b-icon-folder-x>
        <h5>No Faculties or Programs Found</h5>
        <p class="text-muted" v-if="searchQuery">
          No matches for "<b>{{ searchQuery }}</b>". Try clearing the search or adding a new faculty.
        </p>
        <p class="text-muted" v-else>
          You have not created any faculties or programs yet. Click "New Faculty" or "Upload CSV" to get started.
        </p>
        <div class="mt-3">
          <button class="btn btn-outline-primary mr-2" @click="openUploadModal">
            <b-icon-upload class="mr-1"></b-icon-upload> Upload CSV
          </button>
          <button class="btn btn-success" @click="openFacultyModal('create')">
            <b-icon-plus-circle class="mr-1"></b-icon-plus-circle> Create Faculty
          </button>
        </div>
      </div>
    </div>

    <div v-else>
      <div
        v-for="faculty in filteredFaculties"
        :key="faculty.uuid"
        class="card faculty-card mb-3 shadow-sm border-0"
        :class="{ 'faculty-deleted': faculty.deleted }"
      >
        <!-- Faculty Header -->
        <div
          class="card-header faculty-header d-flex flex-wrap align-items-center justify-content-between py-3"
          @click="toggleFacultyExpand(faculty.uuid)"
        >
          <div class="d-flex align-items-center flex-grow-1 pr-3">
            <span class="badge badge-dark code-badge mr-2">{{ faculty.ref }}</span>
            <h5 class="mb-0 faculty-title font-weight-bold mr-2">{{ faculty.name }}</h5>
            <span class="badge badge-pill badge-primary mr-2">
              {{ faculty.programmes ? faculty.programmes.length : 0 }} programs
            </span>
            <span v-if="faculty.deleted" class="badge badge-warning mr-2">Deactivated</span>
          </div>

          <div class="faculty-actions mt-2 mt-md-0" @click.stop>
            <button
              class="btn btn-xs btn-outline-success mr-2"
              @click="openProgrammeModal('create', faculty)"
              title="Add program to this faculty"
            >
              <b-icon-plus></b-icon-plus> Add Program
            </button>
            <button
              class="btn btn-xs btn-outline-secondary mr-2"
              @click="openFacultyModal('edit', faculty)"
              title="Edit faculty"
            >
              <b-icon-pencil></b-icon-pencil> Edit
            </button>
            <button
              v-if="!faculty.deleted"
              class="btn btn-xs btn-outline-danger mr-3"
              @click="toggleFacultyStatus(faculty)"
              title="Deactivate faculty"
            >
              Deactivate
            </button>
            <button
              v-else
              class="btn btn-xs btn-outline-success mr-3"
              @click="toggleFacultyStatus(faculty)"
              title="Reactivate faculty"
            >
              Activate
            </button>
            <b-icon-chevron-up v-if="isExpanded(faculty.uuid)" class="expand-icon"></b-icon-chevron-up>
            <b-icon-chevron-down v-else class="expand-icon"></b-icon-chevron-down>
          </div>
        </div>

        <!-- Faculty Description (if available) -->
        <div v-if="faculty.description && isExpanded(faculty.uuid)" class="px-3 pt-2 text-muted small">
          {{ faculty.description }}
        </div>

        <!-- Nested Programs Section -->
        <b-collapse :id="'collapse-' + faculty.uuid" :visible="isExpanded(faculty.uuid)">
          <div class="card-body p-0">
            <div v-if="!faculty.programmes || faculty.programmes.length === 0" class="p-4 text-center text-muted">
              <em>No programs registered under this faculty.</em>
              <div class="mt-2">
                <button
                  class="btn btn-sm btn-outline-success"
                  @click="openProgrammeModal('create', faculty)"
                >
                  <b-icon-plus-circle class="mr-1"></b-icon-plus-circle> Add First Program
                </button>
              </div>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-hover table-sm mb-0 programme-table">
                <thead class="thead-light">
                  <tr>
                    <th style="width: 50px;">#</th>
                    <th style="width: 140px;">Program Code</th>
                    <th>Program Name</th>
                    <th>Description</th>
                    <th style="width: 110px;">Status</th>
                    <th style="width: 160px;" class="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(programme, pIndex) in faculty.programmes"
                    :key="programme.uuid"
                    :class="{ 'table-secondary text-muted': programme.deleted }"
                  >
                    <td>{{ pIndex + 1 }}</td>
                    <td>
                      <span class="badge badge-info monofont">{{ programme.ref }}</span>
                    </td>
                    <td class="font-weight-bold">{{ programme.name }}</td>
                    <td class="text-muted small">{{ programme.description || '—' }}</td>
                    <td>
                      <span v-if="!programme.deleted" class="badge badge-success">Active</span>
                      <span v-else class="badge badge-warning">Deactivated</span>
                    </td>
                    <td class="text-right">
                      <button
                        class="btn btn-xs btn-outline-secondary mr-1"
                        @click="openProgrammeModal('edit', faculty, programme)"
                        title="Edit program"
                      >
                        <b-icon-pencil></b-icon-pencil> edit
                      </button>
                      <button
                        v-if="!programme.deleted"
                        class="btn btn-xs btn-outline-danger"
                        @click="toggleProgrammeStatus(programme)"
                        title="Deactivate program"
                      >
                        deactivate
                      </button>
                      <button
                        v-else
                        class="btn btn-xs btn-outline-success"
                        @click="toggleProgrammeStatus(programme)"
                        title="Activate program"
                      >
                        activate
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </b-collapse>
      </div>
    </div>

    <!-- ============================================================= -->
    <!-- MODAL 1: Create / Edit Faculty Modal                          -->
    <!-- ============================================================= -->
    <b-modal
      id="facultyModal"
      :title="facultyModalMode === 'create' ? 'Add New Faculty' : 'Edit Faculty'"
      hide-footer
      centered
      size="md"
    >
      <form @submit.prevent="submitFacultyForm">
        <div class="form-group">
          <label for="faculty-ref" class="font-weight-bold">
            Faculty Code / Ref <span class="text-danger">*</span>
          </label>
          <input
            id="faculty-ref"
            type="text"
            class="form-control"
            v-model="facultyForm.ref"
            placeholder="e.g. F01"
            required
          />
          <small class="form-text text-muted">Unique code identifying this faculty.</small>
        </div>

        <div class="form-group">
          <label for="faculty-name" class="font-weight-bold">
            Faculty Name <span class="text-danger">*</span>
          </label>
          <input
            id="faculty-name"
            type="text"
            class="form-control"
            v-model="facultyForm.name"
            placeholder="e.g. FACULTY OF AGRICULTURAL SCIENCES"
            required
          />
        </div>

        <div class="form-group">
          <label for="faculty-desc">Description</label>
          <textarea
            id="faculty-desc"
            class="form-control"
            rows="3"
            v-model="facultyForm.description"
            placeholder="Optional description or notes..."
          ></textarea>
        </div>

        <div class="d-flex justify-content-end mt-4">
          <button type="button" class="btn btn-secondary mr-2" @click="$bvModal.hide('facultyModal')">
            Cancel
          </button>
          <button type="submit" class="btn btn-success" :disabled="facultySubmitting">
            <b-spinner small v-if="facultySubmitting" class="mr-1"></b-spinner>
            {{ facultyModalMode === 'create' ? 'Create Faculty' : 'Save Changes' }}
          </button>
        </div>
      </form>
    </b-modal>

    <!-- ============================================================= -->
    <!-- MODAL 2: Create / Edit Program Modal                          -->
    <!-- ============================================================= -->
    <b-modal
      id="programmeModal"
      :title="programmeModalMode === 'create' ? 'Add New Program' : 'Edit Program'"
      hide-footer
      centered
      size="lg"
    >
      <form @submit.prevent="submitProgrammeForm">
        <div class="form-group">
          <label for="prog-faculty" class="font-weight-bold">
            Faculty <span class="text-danger">*</span>
          </label>
          <select
            id="prog-faculty"
            class="form-control"
            v-model="programmeForm.facultyUUID"
            required
          >
            <option value="" disabled>-- Select Faculty --</option>
            <option
              v-for="fac in faculties"
              :key="fac.uuid"
              :value="fac.uuid"
            >
              {{ fac.ref }} - {{ fac.name }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label for="prog-ref" class="font-weight-bold">
            Program Code / Ref <span class="text-danger">*</span>
          </label>
          <input
            id="prog-ref"
            type="text"
            class="form-control"
            v-model="programmeForm.ref"
            placeholder="e.g. P010202"
            required
          />
          <small class="form-text text-muted">Unique code identifying this program.</small>
        </div>

        <div class="form-group">
          <label for="prog-name" class="font-weight-bold">
            Program Name <span class="text-danger">*</span>
          </label>
          <input
            id="prog-name"
            type="text"
            class="form-control"
            v-model="programmeForm.name"
            placeholder="e.g. B.Agric Agricultural Economics and Agro-Business"
            required
          />
        </div>

        <div class="form-group">
          <label for="prog-desc">Description</label>
          <textarea
            id="prog-desc"
            class="form-control"
            rows="3"
            v-model="programmeForm.description"
            placeholder="Optional description or notes..."
          ></textarea>
        </div>

        <div class="d-flex justify-content-end mt-4">
          <button type="button" class="btn btn-secondary mr-2" @click="$bvModal.hide('programmeModal')">
            Cancel
          </button>
          <button type="submit" class="btn btn-success" :disabled="programmeSubmitting">
            <b-spinner small v-if="programmeSubmitting" class="mr-1"></b-spinner>
            {{ programmeModalMode === 'create' ? 'Create Program' : 'Save Changes' }}
          </button>
        </div>
      </form>
    </b-modal>

    <!-- ============================================================= -->
    <!-- MODAL 3: CSV Batch Upload Modal                               -->
    <!-- ============================================================= -->
    <b-modal
      id="uploadModal"
      title="Batch Upload Faculties & Programs (CSV)"
      hide-footer
      centered
      size="lg"
    >
      <div>
        <div class="alert alert-info py-2 small mb-3">
          <div class="font-weight-bold mb-1">
            <b-icon-info-circle class="mr-1"></b-icon-info-circle> Expected CSV Format:
          </div>
          <p class="mb-1 font-italic">The CSV must have the following column headers:</p>
          <code>programmeID,programme name,facultyID,faculty name</code>
          <p class="mt-2 mb-0 text-dark">
            <b>Note:</b> Existing faculties and programs will be safely skipped. Only new records will be created.
          </p>
        </div>

        <!-- Sample preview table -->
        <div class="table-responsive mb-3">
          <table class="table table-bordered table-sm small mb-0 monofont">
            <thead class="thead-light">
              <tr>
                <th>programmeID</th>
                <th>programme name</th>
                <th>facultyID</th>
                <th>faculty name</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>P010202</td>
                <td>B.Agric Agricultural Economics and Agro-Business</td>
                <td>F01</td>
                <td>FACULTY OF AGRICULTURAL SCIENCES</td>
              </tr>
              <tr>
                <td>P020101</td>
                <td>B.A. English</td>
                <td>F02</td>
                <td>FACULTY OF ARTS</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- File Input -->
        <div class="form-group border rounded p-3 bg-light text-center">
          <input
            type="file"
            ref="csvFileInput"
            accept=".csv,text/csv"
            class="d-none"
            @change="handleFileSelected"
          />
          <div v-if="!selectedFile">
            <b-icon-cloud-upload font-scale="2.5" class="text-primary mb-2"></b-icon-cloud-upload>
            <p class="mb-2">Drag and drop your CSV file here, or click to browse</p>
            <button type="button" class="btn btn-outline-primary btn-sm" @click="$refs.csvFileInput.click()">
              Select CSV File
            </button>
          </div>
          <div v-else class="text-left">
            <div class="d-flex align-items-center justify-content-between p-2 bg-white rounded border">
              <div>
                <b-icon-file-earmark-spreadsheet class="text-success mr-2"></b-icon-file-earmark-spreadsheet>
                <b>{{ selectedFile.name }}</b>
                <span class="text-muted small ml-2">({{ (selectedFile.size / 1024).toFixed(1) }} KB)</span>
              </div>
              <button class="btn btn-sm btn-link text-danger" type="button" @click="selectedFile = null">
                Remove
              </button>
            </div>
          </div>
        </div>

        <!-- Upload Results Summary -->
        <div v-if="uploadResult" class="alert alert-success mt-3 mb-0">
          <h6 class="font-weight-bold mb-2">
            <b-icon-check2-all class="mr-1"></b-icon-check2-all> Upload Completed!
          </h6>
          <p class="mb-2">{{ uploadResult.message }}</p>
          <div class="row small">
            <div class="col-6 col-md-3">
              <b>Faculties Created:</b> {{ uploadResult.facultiesCreated }}
            </div>
            <div class="col-6 col-md-3">
              <b>Programs Created:</b> {{ uploadResult.programmesCreated }}
            </div>
            <div class="col-6 col-md-3">
              <b>Faculties Existing:</b> {{ uploadResult.facultiesSkipped }}
            </div>
            <div class="col-6 col-md-3">
              <b>Programs Existing:</b> {{ uploadResult.programmesSkipped }}
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-4">
          <button type="button" class="btn btn-secondary mr-2" @click="$bvModal.hide('uploadModal')">
            Close
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!selectedFile || uploadSubmitting"
            @click="submitCsvUpload"
          >
            <b-spinner small v-if="uploadSubmitting" class="mr-1"></b-spinner>
            <b-icon-upload v-else class="mr-1"></b-icon-upload>
            Upload & Process CSV
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'FacultiesProgrammesAdmin',

  data() {
    return {
      loading: false,
      faculties: [],
      expandedFacultyUuids: {},
      searchQuery: '',

      // Faculty modal
      facultyModalMode: 'create', // 'create' or 'edit'
      facultySubmitting: false,
      facultyForm: {
        uuid: '',
        ref: '',
        name: '',
        description: ''
      },

      // Programme modal
      programmeModalMode: 'create', // 'create' or 'edit'
      programmeSubmitting: false,
      programmeForm: {
        uuid: '',
        facultyUUID: '',
        ref: '',
        name: '',
        description: ''
      },

      // Upload modal
      selectedFile: null,
      uploadSubmitting: false,
      uploadResult: null
    }
  },

  mounted() {
    this.loadFaculties()
  },

  computed: {
    totalProgrammesCount() {
      return this.faculties.reduce((sum, f) => sum + (f.programmes ? f.programmes.length : 0), 0)
    },

    activeProgrammesCount() {
      return this.faculties.reduce((sum, f) => {
        if (!f.programmes) return sum
        return sum + f.programmes.filter(p => !p.deleted).length
      }, 0)
    },

    filteredFaculties() {
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        return this.faculties
      }

      const q = this.searchQuery.toLowerCase().trim()
      return this.faculties
        .map(f => {
          const facultyMatch =
            (f.name && f.name.toLowerCase().includes(q)) ||
            (f.ref && f.ref.toLowerCase().includes(q))

          const matchedProgrammes = (f.programmes || []).filter(p => {
            return (
              (p.name && p.name.toLowerCase().includes(q)) ||
              (p.ref && p.ref.toLowerCase().includes(q))
            )
          })

          if (facultyMatch) {
            return f
          } else if (matchedProgrammes.length > 0) {
            return {
              ...f,
              programmes: matchedProgrammes
            }
          }
          return null
        })
        .filter(Boolean)
    }
  },

  methods: {
    loadFaculties() {
      this.loading = true
      axios
        .get('/admin/faculties-programmes')
        .then(res => {
          if (res.data.status === 'success') {
            this.faculties = res.data.faculties || []
            // Default expand first few faculties if none expanded yet
            if (Object.keys(this.expandedFacultyUuids).length === 0) {
              this.faculties.forEach((f, idx) => {
                if (idx < 3) {
                  this.$set(this.expandedFacultyUuids, f.uuid, true)
                }
              })
            }
          } else {
            this.$swal('Oops!', res.data.message || 'Failed to load faculties', 'error')
          }
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          this.$swal('Oops!', 'An error occurred while fetching faculties', 'error')
        })
    },

    toggleFacultyExpand(uuid) {
      this.$set(this.expandedFacultyUuids, uuid, !this.expandedFacultyUuids[uuid])
    },

    isExpanded(uuid) {
      return !!this.expandedFacultyUuids[uuid]
    },

    expandAll(expand) {
      this.faculties.forEach(f => {
        this.$set(this.expandedFacultyUuids, f.uuid, expand)
      })
    },

    // -------------------------------------------------------------
    // Faculty CRUD
    // -------------------------------------------------------------
    openFacultyModal(mode, faculty = null) {
      this.facultyModalMode = mode
      if (mode === 'edit' && faculty) {
        this.facultyForm = {
          uuid: faculty.uuid,
          ref: faculty.ref,
          name: faculty.name,
          description: faculty.description || ''
        }
      } else {
        this.facultyForm = {
          uuid: '',
          ref: '',
          name: '',
          description: ''
        }
      }
      this.$bvModal.show('facultyModal')
    },

    submitFacultyForm() {
      this.facultySubmitting = true
      const formData = new FormData()
      formData.append('ref', this.facultyForm.ref)
      formData.append('name', this.facultyForm.name)
      formData.append('description', this.facultyForm.description)

      const url =
        this.facultyModalMode === 'create'
          ? '/admin/faculty/create'
          : '/admin/faculty/edit'

      if (this.facultyModalMode === 'edit') {
        formData.append('uuid', this.facultyForm.uuid)
      }

      axios
        .post(url, formData)
        .then(res => {
          if (res.data.status === 'success') {
            this.$swal('Success!', res.data.message, 'success')
            this.$bvModal.hide('facultyModal')
            this.loadFaculties()
          } else {
            this.$swal('Oops!', res.data.message, 'error')
          }
          this.facultySubmitting = false
        })
        .catch(err => {
          this.facultySubmitting = false
          this.$swal('Oops!', 'Operation failed. Please check your inputs.', 'error')
        })
    },

    toggleFacultyStatus(faculty) {
      const isDeleted = faculty.deleted
      const actionText = isDeleted ? 'activate' : 'deactivate'
      const confirmText = `Are you sure you want to ${actionText} "${faculty.name}"?`

      this.$swal({
        title: 'Confirm',
        text: confirmText,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `Yes, ${actionText}`,
        cancelButtonText: 'Cancel'
      }).then(result => {
        if (result.isConfirmed) {
          const request = isDeleted
            ? axios.put(`/admin/faculty/delete/${faculty.uuid}`)
            : axios.post(`/admin/faculty/delete/${faculty.uuid}`)

          request
            .then(res => {
              if (res.data.status === 'success') {
                this.$swal('Success!', res.data.message, 'success')
                this.loadFaculties()
              } else {
                this.$swal('Oops!', res.data.message, 'error')
              }
            })
            .catch(() => {
              this.$swal('Oops!', 'Failed to update faculty status.', 'error')
            })
        }
      })
    },

    // -------------------------------------------------------------
    // Programme CRUD
    // -------------------------------------------------------------
    openProgrammeModal(mode, faculty = null, programme = null) {
      this.programmeModalMode = mode
      if (mode === 'edit' && programme) {
        this.programmeForm = {
          uuid: programme.uuid,
          facultyUUID: faculty ? faculty.uuid : programme.facultyUUID,
          ref: programme.ref,
          name: programme.name,
          description: programme.description || ''
        }
      } else {
        this.programmeForm = {
          uuid: '',
          facultyUUID: faculty ? faculty.uuid : (this.faculties[0] ? this.faculties[0].uuid : ''),
          ref: '',
          name: '',
          description: ''
        }
      }
      this.$bvModal.show('programmeModal')
    },

    submitProgrammeForm() {
      this.programmeSubmitting = true
      const formData = new FormData()
      formData.append('facultyUUID', this.programmeForm.facultyUUID)
      formData.append('ref', this.programmeForm.ref)
      formData.append('name', this.programmeForm.name)
      formData.append('description', this.programmeForm.description)

      const url =
        this.programmeModalMode === 'create'
          ? '/admin/programme/create'
          : '/admin/programme/edit'

      if (this.programmeModalMode === 'edit') {
        formData.append('uuid', this.programmeForm.uuid)
      }

      axios
        .post(url, formData)
        .then(res => {
          if (res.data.status === 'success') {
            this.$swal('Success!', res.data.message, 'success')
            this.$bvModal.hide('programmeModal')
            // Auto expand the faculty where the programme was added
            if (this.programmeForm.facultyUUID) {
              this.$set(this.expandedFacultyUuids, this.programmeForm.facultyUUID, true)
            }
            this.loadFaculties()
          } else {
            this.$swal('Oops!', res.data.message, 'error')
          }
          this.programmeSubmitting = false
        })
        .catch(err => {
          this.programmeSubmitting = false
          this.$swal('Oops!', 'Operation failed. Please check your inputs.', 'error')
        })
    },

    toggleProgrammeStatus(programme) {
      const isDeleted = programme.deleted
      const actionText = isDeleted ? 'activate' : 'deactivate'
      const confirmText = `Are you sure you want to ${actionText} "${programme.name}"?`

      this.$swal({
        title: 'Confirm',
        text: confirmText,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `Yes, ${actionText}`,
        cancelButtonText: 'Cancel'
      }).then(result => {
        if (result.isConfirmed) {
          const request = isDeleted
            ? axios.put(`/admin/programme/delete/${programme.uuid}`)
            : axios.post(`/admin/programme/delete/${programme.uuid}`)

          request
            .then(res => {
              if (res.data.status === 'success') {
                this.$swal('Success!', res.data.message, 'success')
                this.loadFaculties()
              } else {
                this.$swal('Oops!', res.data.message, 'error')
              }
            })
            .catch(() => {
              this.$swal('Oops!', 'Failed to update program status.', 'error')
            })
        }
      })
    },

    // -------------------------------------------------------------
    // CSV Upload
    // -------------------------------------------------------------
    openUploadModal() {
      this.selectedFile = null
      this.uploadResult = null
      this.$bvModal.show('uploadModal')
    },

    handleFileSelected(event) {
      const files = event.target.files
      if (files && files.length > 0) {
        this.selectedFile = files[0]
      }
    },

    submitCsvUpload() {
      if (!this.selectedFile) return

      this.uploadSubmitting = true
      this.uploadResult = null
      const formData = new FormData()
      formData.append('file', this.selectedFile)

      axios
        .post('/admin/faculties-programmes-upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then(res => {
          if (res.data.status === 'success') {
            this.uploadResult = res.data
            this.$swal('Done!', res.data.message, 'success')
            this.loadFaculties()
          } else {
            this.$swal('Oops!', res.data.message, 'error')
          }
          this.uploadSubmitting = false
        })
        .catch(err => {
          this.uploadSubmitting = false
          this.$swal('Oops!', 'Failed to process CSV file.', 'error')
        })
    }
  }
}
</script>

<style scoped>
.pageTitle {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
}

.stat-card {
  border-radius: 8px;
  transition: transform 0.15s ease-in-out;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.stat-label {
  font-size: 0.72rem;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 1.35rem;
  color: #2c3e50;
}

.faculty-card {
  border-radius: 8px;
  overflow: hidden;
}

.faculty-header {
  background-color: #f8f9fa;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid #e9ecef;
}

.faculty-header:hover {
  background-color: #f1f3f5;
}

.faculty-title {
  font-size: 1rem;
  color: #333;
}

.faculty-deleted {
  opacity: 0.75;
}

.code-badge {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.85rem;
  padding: 0.35em 0.6em;
}

.monofont {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.8rem;
}

.expand-icon {
  font-size: 1rem;
  color: #6c757d;
}

.programme-table th {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-top: none;
}

.programme-table td {
  vertical-align: middle;
  font-size: 0.88rem;
}

.btn-xs {
  padding: 0.2rem 0.45rem;
  font-size: 0.75rem;
  line-height: 1.3;
  border-radius: 0.2rem;
}

.spin-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}
</style>
